/**
 * useFocusTrap Hook
 *
 * Traps keyboard focus within a container element (typically a modal).
 * Handles:
 * - Trapping Tab/Shift+Tab navigation within the container
 * - Setting initial focus when the trap activates
 * - Restoring focus to the previously focused element when deactivated
 * - Closing on Escape key press (optional)
 *
 * @param {Object} options
 * @param {boolean} options.isActive - Whether the focus trap is active
 * @param {Function} options.onEscape - Optional callback when Escape is pressed
 * @param {string} options.initialFocusSelector - CSS selector for initial focus element
 * @returns {Object} { containerRef } - Ref to attach to the container element
 */

import { useRef, useEffect, useCallback } from 'react'

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

export function useFocusTrap({
  isActive = false,
  onEscape,
  initialFocusSelector = null,
} = {}) {
  const containerRef = useRef(null)
  const previousActiveElement = useRef(null)

  // Get all focusable elements within container
  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return []
    return Array.from(containerRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
  }, [])

  // Handle Tab key navigation
  const handleKeyDown = useCallback((event) => {
    if (!containerRef.current) return

    // Handle Escape key
    if (event.key === 'Escape' && onEscape) {
      event.preventDefault()
      onEscape()
      return
    }

    // Only trap Tab key
    if (event.key !== 'Tab') return

    const focusableElements = getFocusableElements()
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    // Shift+Tab from first element -> wrap to last
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
      return
    }

    // Tab from last element -> wrap to first
    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
      return
    }
  }, [getFocusableElements, onEscape])

  // Activate/deactivate focus trap
  useEffect(() => {
    if (!isActive) return

    // Store currently focused element
    previousActiveElement.current = document.activeElement

    // Set initial focus
    const setInitialFocus = () => {
      if (!containerRef.current) return

      // Try to focus specified element first
      if (initialFocusSelector) {
        const initialElement = containerRef.current.querySelector(initialFocusSelector)
        if (initialElement) {
          initialElement.focus()
          return
        }
      }

      // Fall back to first focusable element
      const focusableElements = getFocusableElements()
      if (focusableElements.length > 0) {
        focusableElements[0].focus()
      } else {
        // If no focusable elements, focus container itself
        containerRef.current.setAttribute('tabindex', '-1')
        containerRef.current.focus()
      }
    }

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(setInitialFocus, 10)

    // Add keyboard listener
    document.addEventListener('keydown', handleKeyDown)

    // Cleanup
    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('keydown', handleKeyDown)

      // Restore focus to previous element
      if (previousActiveElement.current && typeof previousActiveElement.current.focus === 'function') {
        previousActiveElement.current.focus()
      }
    }
  }, [isActive, initialFocusSelector, getFocusableElements, handleKeyDown])

  return { containerRef }
}

export default useFocusTrap
